(() => {
  const STORAGE_KEY = 'infynite:saved-pieces:v1';

  const readSaved = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(value)) return [];

      const seenHandles = new Set();
      return value.filter((item) => {
        if (!item || typeof item.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/i.test(item.handle) || seenHandles.has(item.handle)) return false;
        seenHandles.add(item.handle);
        return true;
      }).slice(0, 50);
    } catch (error) {
      return [];
    }
  };

  const writeSaved = (items) => {
    try {
      const savedItems = items.slice(0, 50);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedItems));
      window.dispatchEvent(new CustomEvent('infynite:saved-pieces-updated', { detail: savedItems }));
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  };

  if (!customElements.get('infynite-save-button')) {
    customElements.define('infynite-save-button', class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.onToggle = () => this.toggle();
        this.button.addEventListener('click', this.onToggle);
        this.onSavedUpdate = () => this.render();
        this.onStorage = (event) => { if (event.key === STORAGE_KEY) this.render(); };
        window.addEventListener('infynite:saved-pieces-updated', this.onSavedUpdate);
        window.addEventListener('storage', this.onStorage);
        this.render();
      }

      disconnectedCallback() {
        this.button.removeEventListener('click', this.onToggle);
        window.removeEventListener('infynite:saved-pieces-updated', this.onSavedUpdate);
        window.removeEventListener('storage', this.onStorage);
      }

      item() {
        return {
          handle: this.dataset.handle,
          title: this.dataset.title,
          url: this.dataset.url,
          image: this.dataset.image,
          price: this.dataset.price
        };
      }

      toggle() {
        const items = readSaved();
        const index = items.findIndex((item) => item.handle === this.dataset.handle);
        if (index >= 0) items.splice(index, 1);
        else items.unshift(this.item());
        writeSaved(items);
      }

      render() {
        const saved = readSaved().some((item) => item.handle === this.dataset.handle);
        this.button.setAttribute('aria-pressed', String(saved));
        this.button.setAttribute('aria-label', saved ? `Remove ${this.dataset.title} from Saved Pieces` : `Save ${this.dataset.title}`);
        this.querySelector('[data-save-label]').textContent = saved ? 'Saved' : 'Save';
      }
    });
  }

  if (!customElements.get('infynite-saved-list')) {
    customElements.define('infynite-saved-list', class extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-saved-grid]');
        this.empty = this.querySelector('[data-saved-empty]');
        this.onSavedUpdate = () => this.render();
        this.onStorage = (event) => { if (event.key === STORAGE_KEY) this.render(); };
        window.addEventListener('infynite:saved-pieces-updated', this.onSavedUpdate);
        window.addEventListener('storage', this.onStorage);
        this.render();
      }

      disconnectedCallback() {
        window.removeEventListener('infynite:saved-pieces-updated', this.onSavedUpdate);
        window.removeEventListener('storage', this.onStorage);
      }

      validProductUrl(url) {
        return typeof url === 'string' && /^\/products\/[a-z0-9][a-z0-9-]*([?#].*)?$/i.test(url);
      }

      validImageUrl(url) {
        return typeof url === 'string' && (/^https:\/\//i.test(url) || /^\/\//.test(url));
      }

      card(item) {
        const article = document.createElement('article');
        article.className = 'infynite-saved-card';

        const link = document.createElement('a');
        link.href = this.validProductUrl(item.url) ? item.url : `/products/${encodeURIComponent(item.handle)}`;
        link.className = 'infynite-saved-card__media';

        if (this.validImageUrl(item.image)) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title || '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.fetchPriority = 'low';
          image.width = 600;
          image.height = 750;
          link.append(image);
        }

        const content = document.createElement('div');
        content.className = 'infynite-saved-card__content';
        const heading = document.createElement('h3');
        heading.className = 'h5';
        const title = document.createElement('a');
        title.href = link.href;
        title.textContent = item.title || item.handle;
        heading.append(title);
        const price = document.createElement('p');
        price.className = 'text-subdued';
        price.textContent = item.price ? `Saved at ${item.price}. Check the product page for current price and availability.` : 'Check current price and availability on the product page.';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'link';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${item.title || item.handle} from Saved Pieces`);
        remove.addEventListener('click', () => writeSaved(readSaved().filter((saved) => saved.handle !== item.handle)));
        content.append(heading, price, remove);
        article.append(link, content);
        return article;
      }

      render() {
        const items = readSaved();
        this.empty.hidden = items.length > 0;
        this.grid.hidden = items.length === 0;
        this.grid.replaceChildren(...items.map((item) => this.card(item)));
      }
    });
  }
})();
